import { Globe, Sparkles, Target, Zap, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { CopyButton } from './CopyButton';
import type { LandingPageContent } from '../../types/results';

interface LandingPageViewProps {
  content: LandingPageContent | null;
}

export function LandingPageView({ content }: LandingPageViewProps) {
  // Empty state
  if (!content) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 rounded-full bg-slate-800 flex items-center justify-center mx-auto mb-4">
            <Globe className="w-8 h-8 text-slate-600" />
          </div>
          <h3 className="text-xl font-semibold text-slate-300 mb-2">
            Landing Page Content Not Available
          </h3>
          <p className="text-slate-400 mb-4">
            Landing page content will appear here after your next submission.
          </p>
          <p className="text-sm text-slate-500">
            Make.com now generates this content automatically as part of the workflow.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Hero Section - Title & Subtitle */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="relative rounded-2xl overflow-hidden"
        style={{
          background: '#0A1F18',
          boxShadow:
            '0 0 44px rgba(34,197,94,0.28), 0 0 90px rgba(20,184,166,0.2), inset 0 1px 0 rgba(255,255,255,0.05)',
        }}
      >
        {/* Gradient overlay — neon green to teal */}
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(135deg, rgba(34,197,94,0.32), rgba(20,184,166,0.24) 55%, rgba(34,197,94,0.14))',
          }}
        />

        {/* Bloom glow behind content */}
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute left-[6%] top-[14%] w-[60%] h-[70%] rounded-full blur-[100px] bg-green-500/35" />
          <div className="absolute right-[6%] bottom-[-10%] w-[45%] h-[70%] rounded-full blur-[110px] bg-teal-400/30" />
        </div>

        {/* Inner border ring */}
        <div
          className="absolute inset-0 rounded-2xl pointer-events-none"
          style={{
            boxShadow: 'inset 0 0 0 1px rgba(74,222,128,0.5)',
          }}
        />

        {/* Grid pattern (neon green) */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(74,222,128,0.08)_1px,transparent_1px),linear-gradient(to_bottom,rgba(74,222,128,0.08)_1px,transparent_1px)] bg-[size:28px_28px]" />

        {/* Top highlight line */}
        <div
          className="absolute inset-x-0 top-0 h-px"
          style={{
            background:
              'linear-gradient(to right, transparent, rgba(74,222,128,0.9), transparent)',
          }}
        />

        {/* Content */}
        <div className="relative p-6 md:p-8">
          {/* Badge */}
          <div
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg mb-4"
            style={{
              background: 'rgba(34,197,94,0.2)',
              border: '1px solid rgba(74,222,128,0.55)',
              boxShadow: '0 0 18px rgba(34,197,94,0.4)',
            }}
          >
            <Sparkles className="w-4 h-4" style={{ color: '#86efac' }} />
            <span
              className="text-xs font-bold uppercase tracking-wider"
              style={{ color: '#bbf7d0' }}
            >
              Landing Page Hero
            </span>
          </div>

          {/* Title */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-medium text-slate-400">Main Headline</h4>
              <CopyButton text={content.title} variant="small" />
            </div>
            <h2
              className="text-2xl md:text-3xl lg:text-4xl font-bold text-white leading-tight"
              style={{ textShadow: '0 0 32px rgba(34,197,94,0.45)' }}
            >
              {content.title}
            </h2>
          </div>

          {/* Subtitle */}
          {content.subtitle && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-medium text-slate-400">Supporting Subtitle</h4>
                <CopyButton text={content.subtitle} variant="small" />
              </div>
              <p className="text-base md:text-lg text-slate-100 leading-relaxed max-w-3xl">
                {content.subtitle}
              </p>
            </div>
          )}
        </div>
      </motion.div>

      {/* Description Section */}
      {content.description && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="rounded-2xl bg-slate-900/50 backdrop-blur-xl border border-slate-700/50 p-6"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center"
                style={{
                  background: 'rgba(37,99,235,0.12)',
                  border: '1px solid rgba(37,99,235,0.35)',
                  boxShadow: '0 0 16px rgba(37,99,235,0.25)',
                }}
              >
                <ArrowRight className="w-5 h-5" style={{ color: '#93c5fd' }} />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">Page Description</h3>
                <p className="text-sm text-slate-400">Supporting copy for your registration page</p>
              </div>
            </div>
            <CopyButton text={content.description} variant="small" label="Copy" />
          </div>
          <p className="text-slate-300 leading-relaxed">{content.description}</p>
        </motion.div>
      )}

      {/* Key Hooks */}
      {content.hooks.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="rounded-2xl bg-slate-900/50 backdrop-blur-xl border border-slate-700/50 p-6"
        >
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-3">
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center"
                style={{
                  background: 'rgba(37,99,235,0.12)',
                  border: '1px solid rgba(37,99,235,0.35)',
                  boxShadow: '0 0 16px rgba(37,99,235,0.25)',
                }}
              >
                <Zap className="w-5 h-5" style={{ color: '#93c5fd' }} />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">Key Hooks</h3>
                <p className="text-sm text-slate-400">Curiosity-driven hooks for your registration page</p>
              </div>
            </div>
            <CopyButton
              text={content.hooks.map(h => `\u2022 ${h}`).join('\n')}
              variant="small"
              label="Copy Hooks"
            />
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            {content.hooks.map((hook, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.15 + idx * 0.05 }}
                className="group relative p-4 rounded-xl border border-white/10 transition-all duration-300 hover:shadow-[0_0_20px_rgba(37,99,235,0.15)]"
                style={{ background: '#0A1022' }}
              >
                <div
                  className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity"
                  style={{ background: 'linear-gradient(to bottom right, rgba(37,99,235,0.05), transparent)' }}
                />
                <div className="relative flex items-start gap-3">
                  <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 mt-0.5"
                    style={{
                      background: 'rgba(37,99,235,0.12)',
                      border: '1px solid rgba(37,99,235,0.35)',
                      boxShadow: '0 0 16px rgba(37,99,235,0.25)',
                    }}
                  >
                    <Target className="w-4 h-4" style={{ color: '#93c5fd' }} />
                  </div>
                  <p className="text-slate-200 leading-relaxed flex-1">{hook}</p>
                  <CopyButton text={hook} variant="small" className="opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Three Secrets */}
      {content.secrets.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500/25 to-orange-500/20 border border-amber-500/30 flex items-center justify-center shadow-[0_0_12px_rgba(245,158,11,0.2)]">
                <Sparkles className="w-5 h-5 text-amber-300" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">Three Secrets</h3>
                <p className="text-sm text-slate-400">High-impact reveals for your sales page</p>
              </div>
            </div>
            <CopyButton
              text={content.secrets.map((s, i) => `Secret #${i + 1}: ${s.title}\n\n${s.paragraph}`).join('\n\n---\n\n')}
              label="Copy All Secrets"
            />
          </div>

          <div className="grid md:grid-cols-3 gap-5">
            {content.secrets.map((secret, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 + idx * 0.1 }}
                className="relative rounded-2xl bg-slate-900/50 backdrop-blur-xl border border-slate-700/50 p-6 hover:border-amber-500/30 hover:shadow-[0_0_20px_rgba(245,158,11,0.1)] transition-all group overflow-hidden"
              >
                {/* Top accent line */}
                <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-amber-500/40 to-transparent" />

                {/* Secret number badge */}
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center text-white text-sm font-bold shadow-lg shadow-amber-500/20">
                    {idx + 1}
                  </div>
                  <span className="text-xs font-semibold text-amber-400/80 uppercase tracking-wider">
                    Secret
                  </span>
                </div>

                {/* Secret title */}
                <h4 className="text-lg font-bold text-white mb-3 leading-tight group-hover:text-amber-50 transition-colors">
                  {secret.title}
                </h4>

                {/* Secret paragraph */}
                <p className="text-slate-300 text-sm leading-relaxed mb-4">
                  {secret.paragraph}
                </p>

                {/* Copy button */}
                <div className="pt-3 border-t border-slate-700/50">
                  <CopyButton
                    text={`Secret #${idx + 1}: ${secret.title}\n\n${secret.paragraph}`}
                    variant="small"
                    label="Copy This Secret"
                  />
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      )}

    </div>
  );
}
